import * as Sentry from "@sentry/react";
import { showErrorNotification } from "./notifications";

/**
 * True when the error indicates plan quota / payment required (HTTP 402 or
 * GraphQL PAYMENT_REQUIRED), including plain {@link Error} messages that embed
 * a 402 status (e.g. streamed generation errors).
 */
export const isPaymentRequiredLikeError = (error: unknown): boolean => {
	if (error instanceof Error && /\bstatus code 402\b/i.test(error.message)) {
		return true;
	}
	if (!error || typeof error !== "object") {
		return false;
	}
	const network402 =
		"networkError" in error &&
		error.networkError !== null &&
		typeof error.networkError === "object" &&
		"statusCode" in error.networkError &&
		(error.networkError as { statusCode: number }).statusCode === 402;
	if (network402) {
		return true;
	}
	const graphQLErrors = (error as Record<string, unknown>).graphQLErrors;
	if (
		Array.isArray(graphQLErrors) &&
		graphQLErrors.some(
			(e) =>
				(e && typeof e === "object"
					? (e as { extensions?: { code?: string } }).extensions?.code
					: undefined) === "PAYMENT_REQUIRED",
		)
	) {
		return true;
	}
	return false;
};

/**
 * Best user-facing detail from an Apollo / network error.
 */
const getErrorDetailMessage = (error: Error): string => {
	const graphQLErrors = (error as unknown as Record<string, unknown>)
		.graphQLErrors as Array<{ message?: string }> | undefined;
	return graphQLErrors?.[0]?.message ?? error.message;
};

/**
 * True if the error represents an intentional cancellation (AbortController / navigation).
 */
export const isAbortError = (error: unknown): boolean => {
	if (!error || typeof error !== "object") {
		return false;
	}
	const err = error as { name?: unknown; message?: unknown };
	if (err.name === "AbortError") {
		return true;
	}
	if (typeof err.message === "string") {
		const msg = err.message.toLowerCase();
		return msg.includes("aborted") || msg.includes("abort");
	}
	return false;
};

/**
 * True if the error is a network connectivity failure while the browser is offline.
 */
export const isOfflineNetworkError = (error: unknown): boolean => {
	if (typeof window === "undefined" || window.navigator?.onLine !== false) {
		return false;
	}
	const msg =
		error instanceof Error
			? error.message
			: typeof error === "object" && error !== null && "message" in error
				? String((error as { message?: unknown }).message)
				: typeof error === "string"
					? error
					: "";
	return (
		msg.includes("Failed to fetch") ||
		msg.includes("NetworkError") ||
		msg.includes("Load failed")
	);
};

/**
 * Handle errors and capture them with Sentry.
 *
 * @param error - The error to handle
 * @param message - Optional title; when set, error details go in the body
 */
export const handleError = (error: Error, message?: string) => {
	// 402 / PAYMENT_REQUIRED errors are already surfaced by errorLink (which lets them
	// propagate here). Show a plan-specific message instead of the generic one.
	if (isPaymentRequiredLikeError(error)) {
		const graphQLErrors = (error as unknown as Record<string, unknown>)
			.graphQLErrors as Array<{ message?: string }> | undefined;
		const serverMessage = graphQLErrors?.[0]?.message;
		showErrorNotification(
			"Plan limit reached",
			serverMessage ?? "Upgrade your plan to use this feature",
		);
		return;
	}

	const detailMessage = getErrorDetailMessage(error);
	showErrorNotification(message ?? "Error", detailMessage);
	if (isAbortError(error) || isOfflineNetworkError(error)) {
		return;
	}
	Sentry.captureException(error);
};
